var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { contains, getDocument, isMouseLikePointerType } from "./utils";
import { isElement } from "@floating-ui/utils/dom";
import * as React from "react";
import useModernLayoutEffect from "use-isomorphic-layout-effect";
import { useFloatingParentNodeId, useFloatingTree } from "@floating-ui/react";
import { createAttribute } from "./utils";
import { clearTimeoutIfSet } from "./utils";
import { useLatestRef } from "./utils";
import { useEffectEvent } from "./utils";
var safePolygonIdentifier = createAttribute("safe-polygon");
export function getDelay(value, prop, pointerType) {
    if (pointerType && !isMouseLikePointerType(pointerType)) {
        return 0;
    }
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "function") {
        var result = value();
        if (typeof result === "number") {
            return result;
        }
        return result === null || result === void 0 ? void 0 : result[prop];
    }
    return value === null || value === void 0 ? void 0 : value[prop];
}
function getRestMs(value) {
    if (typeof value === "function") {
        return value();
    }
    return value;
}
/**
 * Opens the floating element while hovering over the reference element, like
 * CSS `:hover`.
 * @see https://floating-ui.com/docs/useHover
 */
export function useHoverLeave(context, props) {
    if (props === void 0) { props = {}; }
    var open = context.open, onOpenChange = context.onOpenChange, dataRef = context.dataRef, events = context.events, elements = context.elements;
    var _a = props.enabled, enabled = _a === void 0 ? true : _a, _b = props.delay, delay = _b === void 0 ? 0 : _b, _c = props.handleClose, handleClose = _c === void 0 ? null : _c, _d = props.mouseOnly, mouseOnly = _d === void 0 ? false : _d, _e = props.restMs, restMs = _e === void 0 ? 0 : _e, _f = props.move, move = _f === void 0 ? true : _f;
    var tree = useFloatingTree();
    var parentId = useFloatingParentNodeId();
    var handleCloseRef = useLatestRef(handleClose);
    var delayRef = useLatestRef(delay);
    var openRef = useLatestRef(open);
    var restMsRef = useLatestRef(restMs);
    var pointerTypeRef = React.useRef();
    var timeoutRef = React.useRef(-1);
    var handlerRef = React.useRef();
    var restTimeoutRef = React.useRef(-1);
    var blockMouseMoveRef = React.useRef(true);
    var performedPointerEventsMutationRef = React.useRef(false);
    var unbindMouseMoveRef = React.useRef(function () { });
    var restTimeoutPendingRef = React.useRef(false);
    var isHoverOpen = React.useCallback(function () {
        var _a;
        var type = (_a = dataRef.current.openEvent) === null || _a === void 0 ? void 0 : _a.type;
        return (type === null || type === void 0 ? void 0 : type.includes("mouse")) && type !== "mousedown";
    }, [dataRef]);
    // When closing before opening, clear the delay timeouts to cancel it
    // from showing.
    React.useEffect(function () {
        if (!enabled)
            return;
        function onOpenChange(_a) {
            var open = _a.open;
            if (!open) {
                clearTimeoutIfSet(timeoutRef);
                clearTimeoutIfSet(restTimeoutRef);
                blockMouseMoveRef.current = true;
                restTimeoutPendingRef.current = false;
            }
        }
        events.on("openchange", onOpenChange);
        return function () {
            events.off("openchange", onOpenChange);
        };
    }, [enabled, events]);
    React.useEffect(function () {
        if (!enabled)
            return;
        if (!handleCloseRef.current)
            return;
        if (!open)
            return;
        function onLeave(event) {
            onOpenChange(false, event, "hover");
        }
        var html = getDocument(elements.floating).documentElement;
        html.addEventListener("mouseleave", onLeave);
        return function () {
            html.removeEventListener("mouseleave", onLeave);
        };
    }, [
        elements.floating,
        open,
        onOpenChange,
        enabled,
        handleCloseRef,
        isHoverOpen,
    ]);
    var closeWithDelay = React.useCallback(function (event, runElseBranch, reason) {
        if (runElseBranch === void 0) { runElseBranch = true; }
        if (reason === void 0) { reason = "hover"; }
        var closeDelay = getDelay(delayRef.current, "close", pointerTypeRef.current);
        if (closeDelay && !handlerRef.current) {
            clearTimeoutIfSet(timeoutRef);
            timeoutRef.current = window.setTimeout(function () { return onOpenChange(false, event, reason); }, closeDelay);
        }
        else if (runElseBranch) {
            clearTimeoutIfSet(timeoutRef);
            onOpenChange(false, event, reason);
        }
    }, [delayRef, onOpenChange]);
    var cleanupMouseMoveHandler = useEffectEvent(function () {
        unbindMouseMoveRef.current();
        handlerRef.current = undefined;
    });
    var clearPointerEvents = useEffectEvent(function () {
        if (performedPointerEventsMutationRef.current) {
            var body = getDocument(elements.floating).body;
            body.style.pointerEvents = "";
            body.removeAttribute(safePolygonIdentifier);
            performedPointerEventsMutationRef.current = false;
        }
    });
    // Registering the mouse events on the reference directly to bypass React's
    // delegation system. If the cursor was on a disabled element and then entered
    // the reference (no gap), `mouseenter` doesn't fire in the delegation system.
    React.useEffect(function () {
        if (!enabled)
            return;
        function onReferenceMouseLeave(event) {
            unbindMouseMoveRef.current();
            var doc = getDocument(elements.floating);
            clearTimeoutIfSet(restTimeoutRef);
            restTimeoutPendingRef.current = false;
            if (handleCloseRef.current && dataRef.current.floatingContext) {
                // Prevent clearing `onScrollMouseLeave` timeout.
                if (!open) {
                    clearTimeoutIfSet(timeoutRef);
                }
                handlerRef.current = handleCloseRef.current(__assign(__assign({}, dataRef.current.floatingContext), { tree: tree, x: event.clientX, y: event.clientY, onClose: function () {
                        clearPointerEvents();
                        cleanupMouseMoveHandler();
                        closeWithDelay(event, true, "safe-polygon");
                    } }));
                var handler_1 = handlerRef.current;
                doc.addEventListener("mousemove", handler_1);
                unbindMouseMoveRef.current = function () {
                    doc.removeEventListener("mousemove", handler_1);
                };
                return;
            }
            // Allow interactivity without `safePolygon` on touch devices. With a
            // pointer, a short close delay is an alternative, so it should work
            // consistently.
            var shouldClose = pointerTypeRef.current === "touch"
                ? !contains(elements.floating, event.relatedTarget)
                : true;
            if (shouldClose) {
                closeWithDelay(event);
            }
        }
        // Ensure the floating element closes after scrolling even if the pointer
        // did not move.
        // https://github.com/floating-ui/floating-ui/discussions/1692
        function onScrollMouseLeave(event) {
            var _a;
            if (!dataRef.current.floatingContext)
                return;
            (_a = handleCloseRef.current) === null || _a === void 0 ? void 0 : _a.call(handleCloseRef, __assign(__assign({}, dataRef.current.floatingContext), { tree: tree, x: event.clientX, y: event.clientY, onClose: function () {
                    clearPointerEvents();
                    cleanupMouseMoveHandler();
                    closeWithDelay(event);
                } }))(event);
        }
        function onFloatingMouseEnter() {
            clearTimeoutIfSet(timeoutRef);
        }
        function onFloatingMouseLeave(event) {
            closeWithDelay(event, false);
        }
        if (isElement(elements.domReference)) {
            var reference_1 = elements.domReference;
            var floating_1 = elements.floating;
            if (open) {
                reference_1.addEventListener("mouseleave", onScrollMouseLeave);
            }
            //   if (move) {
            //     reference.addEventListener("mousemove", onReferenceMouseEnter, {
            //       once: true,
            //     });
            //   }
            //   reference.addEventListener("mouseenter", onReferenceMouseEnter);
            reference_1.addEventListener("mouseleave", onReferenceMouseLeave);
            if (floating_1) {
                floating_1.addEventListener("mouseleave", onScrollMouseLeave);
                floating_1.addEventListener("mouseenter", onFloatingMouseEnter);
                floating_1.addEventListener("mouseleave", onFloatingMouseLeave);
            }
            return function () {
                if (open) {
                }
                reference_1.removeEventListener("mouseleave", onScrollMouseLeave);
                // if (move) {
                //   reference.removeEventListener("mousemove", onReferenceMouseEnter);
                // }
                // reference.removeEventListener("mouseenter", onReferenceMouseEnter);
                reference_1.removeEventListener("mouseleave", onReferenceMouseLeave);
                if (floating_1) {
                    floating_1.removeEventListener("mouseleave", onScrollMouseLeave);
                    floating_1.removeEventListener("mouseenter", onFloatingMouseEnter);
                    floating_1.removeEventListener("mouseleave", onFloatingMouseLeave);
                }
            };
        }
    }, [
        elements,
        enabled,
        context,
        mouseOnly,
        move,
        closeWithDelay,
        cleanupMouseMoveHandler,
        clearPointerEvents,
        onOpenChange,
        open,
        openRef,
        tree,
        delayRef,
        handleCloseRef,
        dataRef,
        restMsRef,
    ]);
    // Block pointer-events of every element other than the reference and floating
    // while the floating element is open and has a `handleClose` handler. Also
    // handles nested floating elements.
    // https://github.com/floating-ui/floating-ui/issues/1722
    useModernLayoutEffect(function () {
        var _a, _b, _c;
        if (!enabled)
            return;
        if (open &&
            ((_a = handleCloseRef.current) === null || _a === void 0 ? void 0 : _a.__options.blockPointerEvents) &&
            isHoverOpen()) {
            performedPointerEventsMutationRef.current = true;
            var floatingEl_1 = elements.floating;
            if (isElement(elements.domReference) && floatingEl_1) {
                var body_1 = getDocument(elements.floating).body;
                body_1.setAttribute(safePolygonIdentifier, "");
                var ref_1 = elements.domReference;
                var parentFloating = (_c = (_b = tree === null || tree === void 0 ? void 0 : tree.nodesRef.current.find(function (node) { return node.id === parentId; })) === null || _b === void 0 ? void 0 : _b.context) === null || _c === void 0 ? void 0 : _c.elements.floating;
                if (parentFloating) {
                    parentFloating.style.pointerEvents = "";
                }
                body_1.style.pointerEvents = "none";
                ref_1.style.pointerEvents = "auto";
                floatingEl_1.style.pointerEvents = "auto";
                return function () {
                    body_1.style.pointerEvents = "";
                    ref_1.style.pointerEvents = "";
                    floatingEl_1.style.pointerEvents = "";
                };
            }
        }
    }, [enabled, open, parentId, elements, tree, handleCloseRef, isHoverOpen]);
    useModernLayoutEffect(function () {
        if (!open) {
            pointerTypeRef.current = undefined;
            restTimeoutPendingRef.current = false;
            cleanupMouseMoveHandler();
            clearPointerEvents();
        }
    }, [open, cleanupMouseMoveHandler, clearPointerEvents]);
    React.useEffect(function () {
        return function () {
            cleanupMouseMoveHandler();
            clearTimeoutIfSet(timeoutRef);
            clearTimeoutIfSet(restTimeoutRef);
            clearPointerEvents();
        };
    }, [
        enabled,
        elements.domReference,
        cleanupMouseMoveHandler,
        clearPointerEvents,
    ]);
    var reference = React.useMemo(function () {
        function setPointerRef(event) {
            pointerTypeRef.current = event.pointerType;
        }
        return {
            onPointerDown: setPointerRef,
            onPointerEnter: setPointerRef,
            onMouseMove: function (event) {
                var nativeEvent = event.nativeEvent;
                function handleMouseMove() {
                    if (!blockMouseMoveRef.current && !openRef.current) {
                        onOpenChange(true, nativeEvent, "hover");
                    }
                }
                if (mouseOnly && !isMouseLikePointerType(pointerTypeRef.current)) {
                    return;
                }
                if (open || getRestMs(restMsRef.current) === 0) {
                    return;
                }
                // Ignore insignificant movements to account for tremors.
                if (restTimeoutPendingRef.current &&
                    Math.pow(event.movementX, 2) + Math.pow(event.movementY, 2) < 2) {
                    return;
                }
                clearTimeoutIfSet(restTimeoutRef);
                if (pointerTypeRef.current === "touch") {
                    handleMouseMove();
                }
                else {
                    restTimeoutPendingRef.current = true;
                    restTimeoutRef.current = window.setTimeout(handleMouseMove, getRestMs(restMsRef.current));
                }
            },
        };
    }, [mouseOnly, onOpenChange, open, openRef, restMsRef]);
    return React.useMemo(function () { return (enabled ? { reference: reference } : {}); }, [enabled, reference]);
}
